<template>
  <v-container fluid class="blog-editor-container">
    <!-- Header -->
    <v-card outlined class="mb-4">
      <v-card-title class="d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-btn icon @click="$router.go(-1)" class="mr-3">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-icon left color="primary">mdi-post</v-icon>
          <span class="text-h6">{{ isEditing ? 'Edit Blog' : 'Create New Blog' }}</span>
        </div>
        <div class="d-flex align-center">
          <v-btn 
            text 
            @click="saveDraft" 
            :loading="saving"
            :disabled="saving"
            class="mr-2"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save Draft
          </v-btn>
          <v-btn 
            color="primary" 
            @click="publishBlog"
            :loading="saving"
            :disabled="saving || !isValid"
          >
            <v-icon left>mdi-publish</v-icon>
            {{ isEditing ? 'Update' : 'Publish' }}
          </v-btn>
        </div>
      </v-card-title>
    </v-card>

    <v-row>
      <!-- Main Editor Area -->
      <v-col cols="12" lg="8">
        <v-card outlined class="blog-editor-card">
          <!-- Featured Image Section -->
          <div class="featured-image-section pa-4">
            <div class="d-flex align-center justify-space-between mb-3">
              <div class="subtitle-1 font-weight-bold">
                <v-icon small color="primary" class="mr-2">mdi-image</v-icon>
                Featured Image
              </div>
              <v-btn
                v-if="blogData.image"
                small
                text
                color="error"
                @click="removeFeaturedImage"
              >
                <v-icon left x-small>mdi-delete</v-icon>
                Remove
              </v-btn>
            </div>
            
            <div v-if="blogData.image" class="featured-image-preview">
              <v-img
                :src="blogData.image"
                alt="Featured Image"
                height="300"
                class="rounded-lg"
                cover
              />
            </div>
            
            <div v-else class="featured-image-upload">
              <v-card outlined class="upload-area pa-6 text-center" @click="initCloudinaryWidget">
                <v-icon size="48" color="grey lighten-1" class="mb-3">mdi-image-plus</v-icon>
                <div class="subtitle-1 grey--text mb-2">Add a featured image</div>
                <div class="caption grey--text">Click to upload</div>
              </v-card>
            </div>
          </div>

          <!-- Title Input -->
          <div class="title-section pa-4">
            <v-text-field
              v-model="blogData.title"
              placeholder="Enter your blog title..."
              class="blog-title-input"
              :rules="[rules.required]"
              hide-details="auto"
              variant="plain"
              density="compact"
              style="font-size: 2.5rem; font-weight: 700;"
            />
          </div>

          <!-- Rich Text Editor -->
          <div class="editor-section pa-4">
            <div class="subtitle-2 font-weight-bold mb-3">
              <v-icon small color="primary" class="mr-2">mdi-pencil</v-icon>
              Content
            </div>
            
            <vue-editor
              v-model="blogData.content"
              :editor-toolbar="editorToolbar"
              :editor-options="editorOptions"
              placeholder="Write your blog content here..."
              class="blog-content-editor"
            />
            
            <!-- Word Count -->
            <div class="text-right mt-2">
              <v-chip size="small" :color="wordCount > 500 ? 'error' : 'default'">
                {{ wordCount }} words
              </v-chip>
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- Sidebar -->
      <v-col cols="12" lg="4">
        <v-card outlined class="sidebar-card">
          <v-card-title>
            <v-icon left>mdi-cog</v-icon>
            Blog Settings
          </v-card-title>

          <v-card-text>
            <!-- Publish Date & Time -->
            <div class="mb-4">
              <div class="subtitle-2 font-weight-bold mb-2">Publish Date & Time</div>
              <v-row>
                <v-col cols="12" md="6">
                  <v-menu
                    v-model="dateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="blogData.publishedDate"
                        label="Date"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        dense
                        :rules="[rules.required]"
                      />
                    </template>
                    <v-date-picker
                      v-model="blogData.publishedDate"
                      @input="dateMenu = false"
                    />
                  </v-menu>
                </v-col>
                <v-col cols="12" md="6">
                  <v-menu
                    v-model="timeMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="blogData.publishedTime"
                        label="Time"
                        prepend-inner-icon="mdi-clock"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        dense
                        :rules="[rules.required]"
                      />
                    </template>
                    <v-time-picker
                      v-model="blogData.publishedTime"
                      @click:minute="timeMenu = false"
                      format="24hr"
                    />
                  </v-menu>
                </v-col>
              </v-row>
            </div>

            <!-- Status -->
            <div class="mb-4">
              <div class="subtitle-2 font-weight-bold mb-2">Status</div>
              <v-select
                v-model="blogData.status"
                :items="statusOptions"
                outlined
                dense
                :rules="[rules.required]"
              />
            </div>

            <!-- SEO Settings -->
            <div class="mb-4">
              <div class="subtitle-2 font-weight-bold mb-2 d-flex align-center justify-space-between">
                SEO Settings
                <div class="d-flex">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        small
                        text
                        color="primary"
                        @click="autoGenerateAll"
                        :loading="generatingSEO"
                        :disabled="generatingSEO || !blogData.content || !blogData.title"
                        v-bind="attrs"
                        v-on="on"
                        class="mr-1"
                      >
                        <v-icon left x-small>mdi-magic-staff</v-icon>
                        All
                      </v-btn>
                    </template>
                    <span>Auto-generate all SEO elements</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        small
                        text
                        color="primary"
                        @click="autoGenerateSEO"
                        :loading="generatingSEO"
                        :disabled="generatingSEO || !blogData.content || !blogData.title"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon left x-small>mdi-auto-fix</v-icon>
                        SEO
                      </v-btn>
                    </template>
                    <span>Auto-generate SEO title and description</span>
                  </v-tooltip>
                </div>
              </div>
              <v-text-field
                v-model="blogData.seoTitle"
                label="SEO Title"
                outlined
                dense
                placeholder="SEO optimized title"
                hint="Leave empty to use blog title"
                persistent-hint
                :counter="60"
              />
              <v-textarea
                v-model="blogData.seoDescription"
                label="SEO Description"
                outlined
                dense
                rows="3"
                placeholder="Brief description for search engines"
                hint="Leave empty to auto-generate"
                persistent-hint
                :counter="160"
              />
            </div>

            <!-- Tags -->
            <div class="mb-4">
              <div class="subtitle-2 font-weight-bold mb-2 d-flex align-center justify-space-between">
                Tags
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      small
                      text
                      color="primary"
                      @click="autoGenerateTags"
                      :loading="generatingSEO"
                      :disabled="generatingSEO || !blogData.content"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon left x-small>mdi-auto-fix</v-icon>
                      Tags
                    </v-btn>
                  </template>
                  <span>Auto-generate relevant tags</span>
                </v-tooltip>
              </div>
              <v-combobox
                v-model="blogData.tags"
                :items="suggestedTags"
                label="Add tags"
                multiple
                chips
                deletable-chips
                small-chips
                outlined
                dense
                placeholder="Add tags to categorize your blog"
              />
            </div>

            <!-- Preview -->
            <div class="mb-4">
              <div class="subtitle-2 font-weight-bold mb-2">Preview</div>
              <v-card outlined class="pa-3" light>
                <div class="body-2 grey--text">
                  <strong>Publish Date:</strong> {{ formattedPublishedDateTime || 'Not set' }}
                </div>
                <div class="body-2 grey--text">
                  <strong>Status:</strong> {{ blogData.status || 'Draft' }}
                </div>
                <div class="body-2 grey--text">
                  <strong>Tags:</strong> {{ blogData.tags.length || 0 }} tags
                </div>
              </v-card>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>


  </v-container>
</template>

<script>
import { db } from "@/utils/firebase.utils";
import {
  doc,
  addDoc,
  updateDoc,
  getDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import commonMixins from "@/mixins/common.mixins.js";
import { VueEditor } from "vue2-editor";
import openaiMixin from "@/mixins/openai.mixin.js";

export default {
  name: "BlogEditor",
  components: {
    VueEditor,
  },
  mixins: [commonMixins, openaiMixin],
  data() {
    return {
      isEditing: false,
      saving: false,
      dateMenu: false,
      timeMenu: false,
      
      blogData: {
        title: "",
        content: "",
        image: "",
        publishedDate: "",
        publishedTime: "",
        status: "draft",
        seoTitle: "",
        seoDescription: "",
        tags: [],
      },

      statusOptions: [
        { text: "Draft", value: "draft" },
        { text: "Published", value: "published" },
      ],

      suggestedTags: [
        "Business",
        "Marketing",
        "Tips",
        "News",
        "Tutorial",
        "Guide",
        "Industry",
        "Technology",
        "Customer Service",
        "Product",
        "Service",
      ],

      // Rich Text Editor Configuration
      editorToolbar: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
      ],

      editorOptions: {
        placeholder: 'Write your blog content here...',
        modules: {
          toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            [{ 'align': [] }],
            ['link', 'image'],
            ['clean']
          ]
        }
      },

      rules: {
        required: (v) => !!v || "Required",
        url: (v) => {
          if (!v) return true;
          const urlPattern = /^https?:\/\/.+/;
          return urlPattern.test(v) || "Please enter a valid URL";
        },
      },

      // Cloudinary Widget
      cloudinaryWidget: null,
      
      // Auto-generation states
      generatingSEO: false,
      generatedSEO: {
        title: "",
        description: "",
        tags: []
      },
      autoGenerateTimeout: null, // Debounce timeout for auto-generation
    };
  },

  computed: {
    wordCount() {
      if (!this.blogData.content) return 0;
      // Remove HTML tags and count words
      const textContent = this.blogData.content.replace(/<[^>]*>/g, ' ').trim();
      const words = textContent.split(/\s+/).filter(word => word.length > 0);
      return words.length;
    },
    formattedPublishedDateTime() {
      if (!this.blogData.publishedDate || !this.blogData.publishedTime) return '';
      const dateTime = new Date(`${this.blogData.publishedDate}T${this.blogData.publishedTime}:00`);
      return dateTime.toLocaleString();
    },
    isValid() {
      return this.blogData.title && this.blogData.content && this.blogData.publishedDate && this.blogData.publishedTime;
    },
  },

  props: {
    blogId: {
      type: String,
      default: null
    }
  },

  async created() {
    this.initializeEditor();
    this.loadCloudinaryWidget();
    
    // Check if editing existing blog
    if (this.blogId) {
      this.isEditing = true;
      await this.loadBlog();
    } else {
      this.setDefaultDateTime();
    }
  },

  methods: {
    initializeEditor() {
      // Initialize with default content if creating new blog
      if (!this.isEditing) {
        this.blogData.content = "";
      }
    },

    setDefaultDateTime() {
      const now = new Date();
      this.blogData.publishedDate = now.toISOString().substr(0, 10);
      this.blogData.publishedTime = now.toTimeString().substr(0, 5);
    },

    async loadBlog() {
      try {
        const blogRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "blogs",
          this.blogId
        );
        
        const blogDoc = await getDoc(blogRef);
        if (blogDoc.exists()) {
          const blogData = blogDoc.data();
          const publishedDate = blogData.publishedAt ? new Date(blogData.publishedAt) : new Date();
          
          this.blogData = {
            title: blogData.title || "",
            content: blogData.text || "",
            image: blogData.image || "",
            publishedDate: publishedDate.toISOString().substr(0, 10),
            publishedTime: publishedDate.toTimeString().substr(0, 5),
            status: blogData.status || "draft",
            seoTitle: blogData.seoTitle || "",
            seoDescription: blogData.seoDescription || "",
            tags: blogData.tags || [],
          };
        }
      } catch (error) {
        console.error("Error loading blog:", error);
        this.$snackbar.show("Failed to load blog");
      }
    },

    removeFeaturedImage() {
      this.blogData.image = "";
    },

    async saveDraft() {
      await this.saveBlog("draft");
    },

    async publishBlog() {
      await this.saveBlog("published");
    },

    async saveBlog(status = null) {
      try {
        if (!this.blogData.title || !this.blogData.content) {
          this.$snackbar.show("Please fill in the title and content");
          return;
        }

        this.saving = true;

        // Combine date and time into a single datetime
        const publishedDateTime = new Date(`${this.blogData.publishedDate}T${this.blogData.publishedTime}:00`);
        
        // Validate future date for published status
        const finalStatus = status || this.blogData.status;
        if (finalStatus === 'published' && publishedDateTime < new Date()) {
          this.$snackbar.show("Cannot publish with a past date/time. Please select a future date and time.");
          return;
        }
        
        const blogData = {
          title: this.blogData.title.trim(),
          text: this.blogData.content.trim(),
          image: this.blogData.image,
          publishedAt: publishedDateTime.toISOString(),
          status: finalStatus,
          seoTitle: this.blogData.seoTitle.trim() || this.blogData.title.trim(),
          seoDescription: this.blogData.seoDescription.trim(),
          tags: this.blogData.tags,
          updatedAt: serverTimestamp(),
        };

        if (this.isEditing) {
          // Update existing blog
          const blogRef = doc(
            db,
            "users",
            this.currentUID,
            "fly_workspaces",
            this.WID,
            "blogs",
            this.blogId
          );
          await updateDoc(blogRef, blogData);
          this.$snackbar.show("Blog updated successfully");
        } else {
          // Create new blog
          blogData.createdAt = serverTimestamp();
          const blogsRef = collection(
            db,
            "users",
            this.currentUID,
            "fly_workspaces",
            this.WID,
            "blogs"
          );
          await addDoc(blogsRef, blogData);
          this.$snackbar.show("Blog created successfully");
        }

        // Navigate back to blog management
        this.$router.push('/blog-management');
      } catch (error) {
        console.error("Error saving blog:", error);
        this.$snackbar.show("Failed to save blog");
      } finally {
        this.saving = false;
      }
    },

    // Cloudinary Widget Methods
    loadCloudinaryWidget() {
      if (!window.cloudinary) {
        const script = document.createElement("script");
        script.src = "https://upload-widget.cloudinary.com/global/all.js";
        script.onload = () => {
          console.log("Cloudinary widget loaded");
        };
        document.head.appendChild(script);
      }
    },

    initCloudinaryWidget() {
      if (!window.cloudinary) {
        this.loadCloudinaryWidget();
        setTimeout(() => this.initCloudinaryWidget(), 1000);
        return;
      }

      this.cloudinaryWidget = window.cloudinary.createUploadWidget(
        {
          cloudName: process.env.VUE_APP_CLOUDINARY_CLOUD_NAME,
          uploadPreset: "demo_preset",
          sources: [
            "local",
            "camera",
            "url",
            "dropbox",
            "image_search",
            "shutterstock",
            "gettyimages",
            "istock",
            "unsplash",
            "google_drive",
          ],
          accept: "image/*",
          clientAllowedFormats: ["png", "jpg", "jpeg", "heic", "heif", "webp"],
          multiple: false,
          maxImageFileSize: 20000000,
          tags: [`${this.currentUID}_${this.WID}`],
          folder: `users/${this.currentUID}/${this.WID}/blogs`,
        },
        (error, result) => {
          if (!error && result && result.event === "success") {
            this.blogData.image = result.info.secure_url;
          }
        }
      );

      this.cloudinaryWidget.open();
    },

    async autoGenerateSEO() {
      if (!this.blogData.title || !this.blogData.content) {
        this.$snackbar.show("Please add a title and content first");
        return;
      }

      this.generatingSEO = true;
      try {
        // Clean content by removing HTML tags
        const cleanContent = this.blogData.content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        
        const prompt = `Generate SEO optimized title and meta description for this blog post:

Title: "${this.blogData.title}"
Content: "${cleanContent.substring(0, 800)}..."

Create a compelling SEO title (max 60 characters) and meta description (max 160 characters) that will help this blog post rank well in search engines. Focus on the main keywords and topics.`;

        const response = await this.openAIRequest(prompt);
        
        // Check if response is valid
        if (!response || typeof response !== 'string') {
          throw new Error("Invalid AI response");
        }
        
        // Try to extract title and description from the response
        const lines = response.split('\n').filter(line => line.trim().length > 0);
        
        if (lines.length >= 2) {
          // Use first line as title, second line as description
          const title = lines[0].replace(/^[^:]*:\s*/, '').trim();
          const description = lines[1].replace(/^[^:]*:\s*/, '').trim();
          
          if (title && description) {
            this.blogData.seoTitle = title.substring(0, 60);
            this.blogData.seoDescription = description.substring(0, 160);
            this.$snackbar.show("SEO settings auto-generated successfully!");
          } else {
            // Fallback: use the response as description
            this.blogData.seoDescription = response.substring(0, 160);
            this.$snackbar.show("SEO description generated (title unchanged)");
          }
        } else {
          // Fallback: use the response as description
          this.blogData.seoDescription = response.substring(0, 160);
          this.$snackbar.show("SEO description generated (title unchanged)");
        }
      } catch (error) {
        console.error("Error generating SEO:", error);
        this.$snackbar.show("Failed to auto-generate SEO settings");
      } finally {
        this.generatingSEO = false;
      }
    },

    async autoGenerateTags() {
      if (!this.blogData.content) {
        this.$snackbar.show("Please add content first");
        return;
      }

      this.generatingSEO = true;
      try {
        // Clean content by removing HTML tags
        const cleanContent = this.blogData.content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        
        const prompt = `Generate 5-8 relevant tags for this blog post:

Title: "${this.blogData.title}"
Content: "${cleanContent.substring(0, 600)}..."

Create relevant tags that describe the main topics, keywords, and themes of this blog post. Return only the tags separated by commas, no explanations.`;

        const response = await this.openAIRequest(prompt);
        
        // Check if response is valid
        if (!response || typeof response !== 'string') {
          throw new Error("Invalid AI response");
        }
        
        const generatedTags = response
          .split(',')
          .map(tag => tag.trim())
          .filter(tag => tag.length > 0 && tag.length < 30)
          .slice(0, 8); // Limit to 8 tags
        
        // Add new tags to existing ones, avoiding duplicates
        const existingTags = this.blogData.tags || [];
        const newTags = generatedTags.filter(tag => !existingTags.includes(tag));
        this.blogData.tags = [...existingTags, ...newTags];
        
        this.$snackbar.show(`${newTags.length} new tags generated successfully!`);
      } catch (error) {
        console.error("Error generating tags:", error);
        this.$snackbar.show("Failed to auto-generate tags");
      } finally {
        this.generatingSEO = false;
      }
    },

    async openAIRequest(prompt) {
      try {
        const data = {
          operation: "generateContent",
          textContent: prompt,
          uid: this.currentUID,
          wid: this.WID,
        };
        
        const result = await this.getContentAiGenerate(data);
        return result;
      } catch (error) {
        console.error("OpenAI request failed:", error);
        throw error;
      }
    },

    async autoGenerateAll() {
      if (!this.blogData.title || !this.blogData.content) {
        this.$snackbar.show("Please add a title and content first");
        return;
      }

      this.generatingSEO = true;
      try {
        // Clean content by removing HTML tags
        const cleanContent = this.blogData.content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        
        const prompt = `Generate comprehensive SEO elements for this blog post:

Title: "${this.blogData.title}"
Content: "${cleanContent.substring(0, 800)}..."

Create:
1. SEO title (max 60 characters)
2. Meta description (max 160 characters) 
3. 5-8 relevant tags (comma-separated)

Format your response with each element on a new line.`;

        const response = await this.openAIRequest(prompt);
        
        // Check if response is valid
        if (!response || typeof response !== 'string') {
          throw new Error("Invalid AI response");
        }
        
        // Parse the response by lines
        const lines = response.split('\n').filter(line => line.trim().length > 0);
        
        if (lines.length >= 3) {
          // First line as title, second as description, rest as tags
          const title = lines[0].replace(/^[^:]*:\s*/, '').trim();
          const description = lines[1].replace(/^[^:]*:\s*/, '').trim();
          const tagsLine = lines[2].replace(/^[^:]*:\s*/, '').trim();
          
          if (title && description) {
            this.blogData.seoTitle = title.substring(0, 60);
            this.blogData.seoDescription = description.substring(0, 160);
            
            // Parse tags
            const generatedTags = tagsLine
              .split(',')
              .map(tag => tag.trim())
              .filter(tag => tag.length > 0 && tag.length < 30)
              .slice(0, 8);
            
            // Add new tags to existing ones, avoiding duplicates
            const existingTags = this.blogData.tags || [];
            const newTags = generatedTags.filter(tag => !existingTags.includes(tag));
            this.blogData.tags = [...existingTags, ...newTags];
            
            this.$snackbar.show("All SEO elements auto-generated successfully!");
          } else {
            // Fallback: use response as description
            this.blogData.seoDescription = response.substring(0, 160);
            this.$snackbar.show("SEO description generated (other elements unchanged)");
          }
        } else {
          // Fallback: use response as description
          this.blogData.seoDescription = response.substring(0, 160);
          this.$snackbar.show("SEO description generated (other elements unchanged)");
        }
      } catch (error) {
        console.error("Error generating all SEO:", error);
        this.$snackbar.show("Failed to auto-generate SEO elements");
      } finally {
        this.generatingSEO = false;
      }
    },
  },

  watch: {
    // Auto-generate SEO when content changes significantly
    'blogData.content': {
      handler(newContent, oldContent) {
        // Only auto-generate if content is substantial and user hasn't manually set SEO
        if (newContent && newContent.length > 100 && 
            (!this.blogData.seoTitle || !this.blogData.seoDescription) &&
            this.wordCount > 50) {
          // Debounce the auto-generation
          clearTimeout(this.autoGenerateTimeout);
          this.autoGenerateTimeout = setTimeout(() => {
            if (!this.generatingSEO) {
              this.autoGenerateAll();
            }
          }, 3000); // Wait 3 seconds after user stops typing
        }
      },
      deep: true
    }
  },

  beforeDestroy() {
    // Clean up timeout to prevent memory leaks
    if (this.autoGenerateTimeout) {
      clearTimeout(this.autoGenerateTimeout);
    }
  },
};
</script>

<style scoped>
.blog-editor-container {
  max-width: 1400px;
  margin: 0 auto;
}

.blog-editor-card {
  min-height: 600px;
}

.featured-image-section {
  border-bottom: 1px solid #e0e0e0;
}

.featured-image-upload .upload-area {
  border: 2px dashed #e0e0e0;
  cursor: pointer;
  transition: all 0.3s ease;
}

.featured-image-upload .upload-area:hover {
  border-color: #1976d2;
  background-color: #f5f5f5;
}

.title-section {
  border-bottom: 1px solid #e0e0e0;
}

.blog-title-input {
  font-size: 2.5rem !important;
  font-weight: 700 !important;
}

.blog-title-input .v-input__slot {
  min-height: 80px !important;
  max-height: none !important;
}

.blog-title-input .v-input__slot input {
  max-height: none !important;
  min-height: 80px !important;
  font-size: 2.5rem !important;
  font-weight: 700 !important;
  line-height: 1.2 !important;
  padding-top: 16px !important;
  padding-bottom: 16px !important;
}

/* Override any global input height constraints for blog title */
.blog-title-input :deep(.v-input__slot) {
  min-height: 80px !important;
  max-height: none !important;
}

.blog-title-input :deep(.v-input__slot input) {
  max-height: none !important;
  min-height: 80px !important;
  font-size: 2.5rem !important;
  font-weight: 700 !important;
  line-height: 1.2 !important;
  padding-top: 16px !important;
  padding-bottom: 16px !important;
}

/* Additional overrides to prevent any global CSS interference */
.blog-title-input :deep(.v-text-field__slot) {
  min-height: 80px !important;
  max-height: none !important;
}

.blog-title-input :deep(.v-text-field__slot input) {
  max-height: none !important;
  min-height: 80px !important;
}

/* Override any combo-box or other global input styles */
.blog-title-input :deep(input) {
  max-height: none !important;
  min-height: 80px !important;
  height: auto !important;
}

/* Ensure the input container has proper height */
.title-section .v-text-field {
  min-height: 80px !important;
}

.title-section .v-text-field :deep(.v-input__slot) {
  min-height: 80px !important;
  max-height: none !important;
}

.editor-section {
  min-height: 400px;
}

.blog-content-editor {
  min-height: 400px;
  font-size: 16px;
  line-height: 1.6;
}

/* Rich Text Editor Styles */
.blog-content-editor .ql-editor {
  min-height: 400px;
  font-size: 16px;
  line-height: 1.6;
  font-family: 'Roboto', sans-serif;
}

.blog-content-editor .ql-toolbar {
  border-top: 1px solid #e0e0e0;
  border-left: 1px solid #e0e0e0;
  border-right: 1px solid #e0e0e0;
  background-color: #fafafa;
}

.blog-content-editor .ql-container {
  border-bottom: 1px solid #e0e0e0;
  border-left: 1px solid #e0e0e0;
  border-right: 1px solid #e0e0e0;
  font-size: 16px;
}

.blog-content-editor .ql-editor h1,
.blog-content-editor .ql-editor h2,
.blog-content-editor .ql-editor h3,
.blog-content-editor .ql-editor h4,
.blog-content-editor .ql-editor h5,
.blog-content-editor .ql-editor h6 {
  margin-top: 1em;
  margin-bottom: 0.5em;
  font-weight: 600;
}

.blog-content-editor .ql-editor p {
  margin-bottom: 1em;
}

.blog-content-editor .ql-editor ul,
.blog-content-editor .ql-editor ol {
  margin-bottom: 1em;
  padding-left: 2em;
}

.blog-content-editor .ql-editor blockquote {
  border-left: 4px solid #1976d2;
  margin: 1em 0;
  padding-left: 1em;
  font-style: italic;
  color: #666;
}

.blog-content-editor .ql-editor a {
  color: #1976d2;
  text-decoration: underline;
}

.blog-content-editor .ql-editor img {
  max-width: 100%;
  height: auto;
  margin: 1em 0;
  border-radius: 4px;
}

.sidebar-card {
  position: sticky;
  top: 20px;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .blog-editor-container {
    max-width: 100%;
  }
}

@media (max-width: 768px) {
  .blog-title-input {
    font-size: 1.8rem !important;
  }
  
  .blog-content-editor .ql-editor {
    min-height: 300px;
    font-size: 14px;
  }
}

/* Auto-generation button styles */
.auto-generate-btn {
  transition: all 0.3s ease;
}

.auto-generate-btn:hover {
  transform: scale(1.05);
}

.auto-generate-btn:disabled {
  opacity: 0.6;
}

/* SEO section enhancements */
.seo-section {
  position: relative;
}

.seo-section .v-text-field,
.seo-section .v-textarea {
  transition: all 0.3s ease;
}

.seo-section .v-text-field:focus-within,
.seo-section .v-textarea:focus-within {
  border-color: #1976d2;
}

/* Auto-generation indicator */
.auto-gen-indicator {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 16px;
  height: 16px;
  background-color: #4caf50;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style> 